/**
 * @file ContentCategoryEditDialog.tsx
 * @description Окно «Изменить рубрику блога» (phase19.16, партия Р3в,
 * v0.19.64; SB131; контракт r3v-api-contract §3): название, описание и
 * адрес `/blog/category/<адрес>`; при смене адреса — галочка «Переадресовать
 * со старого адреса» (по умолчанию да). До партии у рубрик не было правки —
 * адрес задавался один раз при создании. Ошибка сервера — под полями; тост о
 * созданной или снятой переадресации. До sm — на весь экран.
 */

import { useEffect, useState } from "react";

import { Button } from "../../../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { NoticeStrip } from "../../../components/ui/NoticeStrip";
import { useNotify } from "../../../components/ui/NotifyProvider";
import { Textarea } from "../../../components/ui/textarea";
import { RedirectOldSlugCheck } from "../../../components/site-seo/RedirectOldSlugCheck";
import { ApiError } from "../../../lib/api-client";
import { contentCategories, type ContentCategory } from "../../../lib/api-content";
import { notifyRedirectChange } from "../../../lib/site-seo/redirect-change";

interface Props {
  category: ContentCategory | null;
  onClose: () => void;
  onSaved: () => void;
}

export function ContentCategoryEditDialog({ category, onClose, onSaved }: Props) {
  const notify = useNotify();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [slug, setSlug] = useState("");
  const [redirect, setRedirect] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!category) return;
    setName(category.name);
    setDescription(category.description);
    setSlug(category.slug);
    setRedirect(true);
    setError(null);
  }, [category]);

  const slugChanged = !!category && slug.trim() !== "" && slug.trim() !== category.slug;

  async function save() {
    if (!category || !name.trim() || !description.trim()) return setError("Название и описание обязательны.");
    setBusy(true);
    setError(null);
    try {
      const saved = await contentCategories.update(category.id, {
        name: name.trim(),
        description: description.trim(),
        ...(slugChanged ? { slug: slug.trim(), redirectFromOldSlug: redirect } : {}),
      });
      notify.success("Рубрика сохранена");
      notifyRedirectChange(saved.redirect, notify);
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось сохранить рубрику.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={category !== null} onOpenChange={(next) => (!next && !busy ? onClose() : null)}>
      <DialogContent fullScreen className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Изменить рубрику</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="cc-name">Название</Label>
          <Input id="cc-name" value={name} disabled={busy} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="cc-desc">Описание</Label>
          <Textarea id="cc-desc" value={description} rows={2} disabled={busy} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="cc-slug">Адрес: /blog/category/…</Label>
          <Input id="cc-slug" value={slug} disabled={busy} onChange={(e) => setSlug(e.target.value.toLowerCase())} />
          <RedirectOldSlugCheck oldPath={`/blog/category/${category?.slug ?? ""}`} changed={slugChanged} checked={redirect} onChange={setRedirect} disabled={busy} />
        </div>
        {error ? <NoticeStrip tone="error">{error}</NoticeStrip> : null}
        <DialogFooter className="flex-row justify-end max-sm:mt-auto">
          <Button variant="ghost" className="max-sm:flex-1" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button className="max-sm:flex-1" loading={busy} onClick={() => void save()}>
            Сохранить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
